import useFilterHook from "hooks/useFilter";
import usePagination from "hooks/usePagination";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { TEST_RATE_COLUMNS_DATA, FILTER_DEFINITIONS } from "./config";
import { DEFAULT_TOTAL_RECORDS, STALE_TIME } from "utils/constants";
import FilterPanelMaster from "./FilterPanelMaster";
import Table from "components/Common/Table/index";
import { useMutation } from "@tanstack/react-query";
import {
  fetchPanelMaster,
  fetchPanelMasterPending,
  updatePanelMaster,
  updatePanelMasterSync,
} from "services/panelMaster";
import { useAlert } from "context/alert";
import Loader from "components/Common/loader";
import { FaSyncAlt } from "react-icons/fa";
import zoneLocalStorageId from "utils/zone";

const PanelMasterApproval = () => {
  const { showAlert } = useAlert();

  const [selectedRows, setSelectedRows] = useState([]);
  const [editableRows, setEditableRows] = useState({});
  const [rate, setRate] = useState("");
  const [mrp, setMrp] = useState("");
  const [ratePercentage, setRatePercentage] = useState("");
  const [mrpPercentage, setMrpPercentage] = useState("");
  const [isFetchingData, setIsFetchingData] = useState(false);
  const [previousData, setPreviousData] = useState([]);
  const [error, setError] = useState(false);

  const [isSuperFranchise] = useState(() =>
    Number(localStorage.getItem("is_super_franchise"))
  );
  const [isFranchise] = useState(() =>
    Number(localStorage.getItem("is_franchise_employee"))
  );
  const [isSubFranchise] = useState(() =>
    Number(localStorage.getItem("sub_franchise"))
  );

  const { currentPage, updateCurrentPage, updateTotalRecords } =
    usePagination();

  const { filters, updateFilter, updateMultipleFilters } = useFilterHook(
    FILTER_DEFINITIONS,
    updateCurrentPage
  );

  const filterRef = useRef(filters);

  const hasRateFilters =
    filters.rate ||
    filters.mrp_rate ||
    filters.rate_percentage ||
    filters.mrp_percentage;

  const shouldShowTable =
    (filters.is_active || filters.is_active === false) &&
    (filters.billing_category !== "" ||
      filters.investigation_id ||
      filters.lab_department_id ||
      filters.franchise_id ||
      filters.zone_id);

  console.log(filters);

  const {
    data: testRateData = [],
    isLoading,
    refetch: refetchTestRate,
  } = useQuery(
    [
      "fetch-PanelMasterApproval",
      filters?.franchise_id,
      filters?.investigation_id,
    ],
    () =>
      fetchPanelMasterPending({
        franchise_id: filters?.franchise_id,
        investigation_id: filters?.investigation_id,
      }),
    {
      keepPreviousData: true,
      staleTime: 5000,
      onSuccess: (res) => {
        updateTotalRecords(res?.data?.additional_data?.total_count || 0);
      },
      select: (res) => {
        const response = res?.data ?? [];
        const defaults = res?.data?.default ?? [];

        if (response.length > 0) {
          const defaultRatesMap = (defaults || []).reduce((acc, item) => {
            acc[item?.investigation_id] = item?.rate || 0;
            return acc;
          }, {});

          return {
            ...res,
            data: {
              ...res.data,
              data: response.map((item, index) => ({
                ...item,
                rates: defaultRatesMap[item?.investigation_id] || 0,
                _index: (currentPage - 1) * DEFAULT_TOTAL_RECORDS + index + 1,
              })),
            },
          };
        } else {
          return res;
        }
      },
      onError: (res) => {
        console.error("Error fetching TestRates List:", res);
      },
      enabled: !!shouldShowTable,
    }
  );

  console.log("testRateData", testRateData);

  useEffect(() => {
    if (
      !isFetchingData &&
      shouldShowTable &&
      !hasRateFilters &&
      filterRef.current !== filters
    ) {
      setIsFetchingData(true);
      refetchTestRate().finally(() => {
        setIsFetchingData(false);
      });
      filterRef.current = filters;
    }
  }, [filters, shouldShowTable, refetchTestRate, isFetchingData, currentPage]);

  useEffect(() => {
    if (testRateData?.data?.data) {
      setPreviousData((prev) => {
        const merged = [
          ...new Map(
            [...prev, ...testRateData.data.data].map((item) => [item.id, item])
          ).values(),
        ];
        return merged;
      });
    }
  }, [testRateData?.data?.data]);

  const queryClient = useQueryClient();

  const onSuccess = () => {
    const message = "Department updated successfully!";
    showAlert(message, "success");
    refetchTestRate();
    queryClient.invalidateQueries(["fetch-PanelMasterApproval"]);
  };

  const onError = useCallback(() => {
    showAlert("Something went wrong!", "danger");
  }, [showAlert]);

  const { mutate: updatePanelMasterMutate } = useMutation({
    mutationFn: updatePanelMaster,
    onSuccess,
    onError,
  });

  const toggleSelectAll = useCallback(
    (isChecked) => {
      if (isChecked) {
        const allRowIds = testRateData?.data?.data.map((row) => row.id);
        setSelectedRows(allRowIds);
        const updatedSelectedData = Array.isArray(testRateData?.data?.data)
          ? testRateData.data.data.filter((row) => allRowIds.includes(row.id))
          : [];
        setPreviousData((prev) => {
          const mergedData = [
            ...new Map(
              [...prev, ...updatedSelectedData].map((item) => [item.id, item])
            ).values(),
          ];
          return mergedData;
        });
        const allEditableRows = {};
        allRowIds.forEach((id) => {
          allEditableRows[id] = true;
        });
        setEditableRows(allEditableRows);
      } else {
        setSelectedRows([]);
        setEditableRows({});
      }
    },
    [testRateData]
  );

  const toggleSelectRow = useCallback(
    (id) => {
      setSelectedRows((prevSelected) => {
        const updatedSelected = prevSelected.includes(id)
          ? prevSelected.filter((rowId) => rowId !== id)
          : [...prevSelected, id];

        const updatedSelectedData = testRateData?.data
          ? testRateData.data.data.filter((row) =>
              updatedSelected.includes(row.id)
            )
          : [];
        setPreviousData((prev) => {
          const mergedData = [
            ...new Map(
              [...prev, ...updatedSelectedData].map((item) => [item.id, item])
            ).values(),
          ];
          return mergedData;
        });

        return updatedSelected;
      });

      setEditableRows((prevEditable) => ({
        ...prevEditable,
        [id]: !prevEditable[id],
      }));
    },
    [testRateData]
  );

  const sortedData = testRateData?.data?.data?.length
    ? testRateData.data.data.sort((a, b) => {
        const isSelectedA = selectedRows.includes(a.id);
        const isSelectedB = selectedRows.includes(b.id);

        if (isSelectedA && !isSelectedB) return -1;
        if (!isSelectedA && isSelectedB) return 1;
        return 0;
      })
    : [];

  const mergedData = [
    ...new Map(
      [...previousData, ...sortedData].map((item) => [item.id, item])
    ).values(),
  ];

  const handleSave = useCallback(() => {
    const payload = selectedRows
      .map((id) => {
        let originalRow = testRateData?.data?.data.find((row) => row.id === id);
        if (!originalRow) {
          originalRow = mergedData.find((row) => row.id === id);
        }
        if (originalRow && !originalRow.error) {
          return {
            id: Number(originalRow.id),
            test_code: originalRow.test_code,
            created_by: Number(originalRow.created_by),
            is_active: Number(originalRow.is_active),
            investigation_id: Number(originalRow.investigation_id),
            test_name: originalRow.test_name,
            rate: Number(originalRow.rate),
            mrp_rate: Number(originalRow.mrp_rate),
            zone_id: Number(originalRow.zone_id || zoneLocalStorageId()),
            franchise_id: Number(originalRow.franchise_id),
            status: "APPROVED",
          };
        }
        return null;
      })
      .filter((row) => row !== null);
    if (payload?.length > 0) {
      updatePanelMasterMutate({ bulk_update_test_rate: payload });
      setRate("");
      setMrp("");
      setRatePercentage("");
      setMrpPercentage("");
      setEditableRows({});
      setSelectedRows([]);
    }
  }, [selectedRows, testRateData?.data?.data, updatePanelMasterMutate]);

  const columns = useMemo(() => {
    return TEST_RATE_COLUMNS_DATA({
      filters,
      toggleSelectAll,
      selectedRows,
      editableRows,
      setEditableRows,
      toggleSelectRow,
      mrp,
      rate,
      mrpPercentage,
      ratePercentage,
      data: testRateData?.data?.data || [],
      isSuperFranchise,
      isFranchise,
      isSubFranchise,
      // error,
      setError,
    });
  }, [
    filters,
    selectedRows,
    testRateData,
    editableRows,
    mrp,
    rate,
    mrpPercentage,
    ratePercentage,
    isSuperFranchise,
    isFranchise,
    isSubFranchise,
    setError,
  ]);

  return (
    <div className="container-fluid mb-3">
      <div className="box mb-4">
        <div className="boxHeader d-flex justify-content-between align-items-center">
          <h1>Franchise Copy Rates Pending List</h1>
          {
            <div className="d-flex align-items-center">
              {testRateData?.data?.data?.length > 6 &&
                !isFetchingData &&
                shouldShowTable && (
                  <button
                    type="submit"
                    onClick={handleSave}
                    disabled={error}
                    className="btn btn-primary me-2"
                  >
                    Save
                  </button>
                )}
              {isSuperFranchise != 1 &&
                isSubFranchise != 1 &&
                isFranchise != 1 && (
                  <button
                    type="button"
                    onClick={() => {
                      refetchTestRate();
                      updatePanelMasterSync();
                    }}
                    className="btn btn-outline-primary d-flex align-items-center"
                    title="Refresh"
                  >
                    <FaSyncAlt />
                  </button>
                )}
            </div>
          }
        </div>

        <div className="boxBody">
          <div className="left d-lg-flex flex-wrap align-items-center w-100">
            <FilterPanelMaster
              updateFilter={updateFilter}
              filters={filters}
              updateMultipleFilters={updateMultipleFilters}
              mrp={mrp}
              rate={rate}
              ratePercentage={ratePercentage}
              mrpPercentage={mrpPercentage}
              setMrp={setMrp}
              setRate={setRate}
              setRatePercentage={setRatePercentage}
              setMrpPercentage={setMrpPercentage}
              isSuperFranchise={isSuperFranchise}
              isFranchise={isFranchise}
              isSubFranchise={isSubFranchise}
            />
            {/* {selectedRows.length > 5 && ( */}
            <div className="ms-auto mt-4">
              <button
                type="submit"
                onClick={handleSave}
                disabled={error || selectedRows.length === 0}
                className="btn btn-primary mt-4"
              >
                Approve
              </button>
            </div>
            {/* )} */}
          </div>
          <hr />
          {!isFetchingData && shouldShowTable && (
            <>
              <div className="mt-3">
                <Table
                  data={testRateData?.data?.data || []}
                  isLoading={isFetchingData}
                  tableColumns={columns}
                  showPagination={false}
                />
              </div>
              {/* {selectedRows.length < 5 && ( */}
              <div>
                <button
                  type="submit"
                  onClick={handleSave}
                  disabled={error || selectedRows.length === 0}
                  className="btn btn-primary mt-4"
                >
                  Approve
                </button>
              </div>
              {/* )} */}
            </>
          )}
          {isFetchingData && <Loader isLoading={true} />}
        </div>
      </div>
    </div>
  );
};

export default PanelMasterApproval;
